import { useApolloClient } from '@apollo/client';
import * as React from 'react';
import { usePreference } from '../preference/PreferenceProvider';
import { PreferenceKey } from '../preference/type';
import { AuthenticatedItemResponse, GET_SESSION } from './gql';
import { CustomSession, School } from './type';

export interface SessionContextType {
    clear: () => Promise<void>;
    session: CustomSession | null;
    setSession: (token: string) => Promise<void>;
}

const SessionContext = React.createContext<SessionContextType | undefined>(undefined);

export interface SchoolContextType {
    school: School | null;
    schools: School[] | null;
    setSchool: (school: School) => void;
}

const SchoolContext = React.createContext<SchoolContextType | undefined>(undefined);

type Props = {
    loadingIndicator?: React.ReactNode;
};

export const SessionProvider: React.FC<React.PropsWithChildren<Props>> = ({ children, loadingIndicator }) => {
    const client = useApolloClient();
    const { get: getPreference, set: setPreference } = usePreference();
    // Note that undefined is used to indicate that the session is initializing and SessionProvider is not yet ready
    const [session, setSession] = React.useState<CustomSession | null | undefined>(undefined);
    const [school, setSchool] = React.useState<School | null>(null);

    const fetchSession = React.useCallback(async () => {
        return client.query<AuthenticatedItemResponse>({ query: GET_SESSION, fetchPolicy: 'network-only' }).then((response) => {
            const session = response.data?.authenticatedItem;
            return session;
        });
    }, [client]);

    // Init
    React.useEffect(() => {
        // Using persitent session token since init won't have any session info in-memory
        const token = localStorage.getItem('token');
        if (token) {
            fetchSession()
                .then(async (item) => {
                    if (item) {
                        setSession({ token, user: item });
                    } else {
                        setSession(null);
                    }
                })
                .catch(async () => {
                    setSession(null);
                });
        } else {
            setSession(null);
        }
    }, [fetchSession]);

    // Schoo change
    React.useEffect(() => {
        if (!session) {
            return;
        }

        /**
         * Precedence: Preference > school.isDefault > schools[0]
         */

        // Preference
        const preference = getPreference(PreferenceKey.School);
        const preferredSchool = preference ? session?.user?.teacherProfile?.schools.find((school) => school.id === preference) : undefined;
        if (preferredSchool) {
            setSchool(preferredSchool);
            return;
        }

        // Default
        const defaultSchool = session?.user?.teacherProfile?.schools.find((school) => school.isDefault);
        if (defaultSchool) {
            setSchool(defaultSchool);
            return;
        }

        // Fallback
        const fallbackSchool = session?.user?.teacherProfile?.schools[0] ?? null;
        setSchool(fallbackSchool);
    }, [getPreference, session]);

    if (session === undefined) {
        return loadingIndicator ? loadingIndicator : null;
    }

    return (
        <SessionContext.Provider
            value={{
                clear: async () => {
                    setSchool(null);
                    localStorage.removeItem('token');
                    await client.resetStore();
                    await setSession(null);
                },
                session,
                setSession: async (token: string) => {
                    localStorage.setItem('token', token);

                    await client.resetStore();
                    const user = await fetchSession();

                    setSession({ token, user });
                },
            }}
        >
            <SchoolContext.Provider
                value={{
                    school,
                    schools: session?.user?.teacherProfile?.schools ?? null,
                    setSchool: (school: School) => {
                        if (school) {
                            setPreference(PreferenceKey.School, school.id);
                        }

                        setSchool(school);
                    },
                }}
            >
                {children}
            </SchoolContext.Provider>
        </SessionContext.Provider>
    );
};

export const useSession = (): SessionContextType => {
    const context = React.useContext(SessionContext);
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider');
    }

    return context;
};

export const useSchool = (): SchoolContextType => {
    const context = React.useContext(SchoolContext);
    if (!context) {
        throw new Error('useSchool must be used within a SessionProvider');
    }

    return context;
};
